import { useState, useCallback, useEffect } from 'react';

/**
 * Custom hook for keyboard navigation in a list
 */
export function useKeyboardNavigation<T>(
  items: T[],
  onSelect: (item: T) => void,
  isOpen: boolean
) {
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);

  // Reset selection when items change or list closes
  useEffect(() => {
    if (!isOpen || items.length === 0) {
      setSelectedIndex(-1);
    }
  }, [items.length, isOpen]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent): boolean => {
      if (!isOpen || items.length === 0) {
        return false;
      }

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          setSelectedIndex(prev => {
            const newIndex = prev < items.length - 1 ? prev + 1 : 0;
            return newIndex;
          });
          return true;

        case 'ArrowUp':
          event.preventDefault();
          setSelectedIndex(prev => {
            const newIndex = prev > 0 ? prev - 1 : items.length - 1;
            return newIndex;
          });
          return true;

        case 'Enter':
          event.preventDefault();
          if (selectedIndex >= 0 && selectedIndex < items.length) {
            onSelect(items[selectedIndex]);
          }
          return true;

        case 'Escape':
          event.preventDefault();
          setSelectedIndex(-1);
          return true;

        default:
          return false;
      }
    },
    [isOpen, items, selectedIndex, onSelect]
  );

  return {
    selectedIndex,
    setSelectedIndex,
    handleKeyDown
  };
}
